import {
  TemplateResponseData,
  useCompany,
  useDealers,
  useLinkDealerToContractType,
  useUnlinkDealerFromContractType,
  useContractType,
  useTemplatesByContractType,
} from '@api'
import {FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef} from '@mui/x-data-grid'
import {errorAtom, successMessageAtom, pageLinksAtom, getCompanyCurrencyAtom, getUserSelectionAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {
  formatCurrency,
  formatPercentage,
  FormFieldItem,
  generateDropdownOptions,
  generateReadableErrorMessages,
  isCompanyUser,
  unlinkButtonCell,
  renderClickableCell,
  useViewNavigate,
  getUserData,
} from 'common'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

import {ComposedComponents, UIKit} from '@components'

export enum ContractTypeTab {
  GENERAL,
  DEALERS,
}

export function useContractTypeViewModel() {
  const setError = useSetAtom(errorAtom)
  const setSuccessMessage = useSetAtom(successMessageAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [contractTemplate, setContractTemplate] = useState<TemplateResponseData | undefined>(undefined)
  const [activeTab, setActiveTab] = useState<ContractTypeTab>(ContractTypeTab.GENERAL)
  const [isLinkModalShown, setIsLinkModalShown] = useState<boolean>(false)
  const [dealerToLink, setDealerToLink] = useState<string | undefined>(undefined)
  const [dealerFormFieldError, setDealerFormFieldError] = useState<string>('')
  const navigate = useViewNavigate()
  const {t} = useTranslation()
  const {id} = useParams()

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const getCompanyCurrency = useAtomValue(getCompanyCurrencyAtom)
  const currency = userSelection ? getCompanyCurrency(userSelection?.companyId) : 'EUR'

  const {data: dealerData} = useDealers()
  const {data: contractTypeData} = useContractType(id)
  const {data: companyData} = useCompany(contractTypeData?.companyId || '')
  const {data: currentTemplate} = useTemplatesByContractType(id)

  useEffect(() => {
    if (currentTemplate && currentTemplate[0]) {
      setContractTemplate(currentTemplate[0])
    }
  }, [currentTemplate])

  const dealerRows = useMemo(() => {
    if (!contractTypeData) return []
    else
      return contractTypeData.allowedDealers.map(item => ({
        name: item.name,
        id: item.id,
        unlinkId: item.id,
      }))
  }, [contractTypeData])

  const {mutate: linkDealerToContractTypeMutation} = useLinkDealerToContractType()
  const {mutate: unlinkDealerFromContractTypeMutation} = useUnlinkDealerFromContractType()

  const dealerDropdownOptions = useMemo(() => {
    if (!dealerData) return []

    return generateDropdownOptions(
      dealerData.filter(item => !contractTypeData?.allowedDealers?.map(dealer => dealer.id).includes(item.id)),
    )
  }, [dealerData, contractTypeData])

  /**
   * Edit button is only available to Administrator users
   */
  const isEditButtonVisible = !isCompanyUser()

  useEffect(() => {
    setTitleLinks([
      {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
      {title: t(TranslationKey.Contracts), link: NavigationPathKey.CONTRACT_DASHBOARD},
      {title: t(TranslationKey['Contract type search']), link: NavigationPathKey.CONTRACT_TYPE_SEARCH},
      {title: t(TranslationKey['Contract Type view'])},
    ])
  }, [])

  /**
   * Dynamically generated modal text
   */
  const modalText: string = useMemo(
    () => `${t(TranslationKey['Choose a dealer to link to'])} ${contractTypeData?.name}`,
    [contractTypeData?.name],
  )

  /**
   * Tab buttons configuration
   */
  const tabButtons: ComposedComponents.TabButtonProps<ContractTypeTab>[] = [
    {
      label: t(TranslationKey['General information']),
      key: ContractTypeTab.GENERAL,
      onClick: () => setActiveTab(ContractTypeTab.GENERAL),
    },
    {
      label: t(TranslationKey.Dealers),
      key: ContractTypeTab.DEALERS,
      onClick: () => setActiveTab(ContractTypeTab.DEALERS),
    },
  ]

  /**
   * Dealers table columns configuration
   */
  const dealerColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 1.5,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.DEALER_VIEW}/${params.row.id}`)),
    },
    ...(isEditButtonVisible ? [unlinkButtonCell(t, id => onUnlink(id))] : []),
  ]

  /**
   * Contract Type form fields configuration
   */
  const contractTypeFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Contract type name']),
      text: contractTypeData?.name,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Company),
      text: companyData?.name,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Type),
      text: contractTypeData?.type || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Valid for']),
      text: contractTypeData?.expiry ? `${contractTypeData?.expiry} ${t(TranslationKey['Day(s)'])}` : '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Contract template']),
      text: contractTemplate?.name || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Contract lengths']),
      text: contractTypeData?.lengths?.length
        ? `${contractTypeData?.lengths?.join(', ')} ${t(TranslationKey['Month(s)'])}`
        : '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Active),
      text: contractTypeData?.active ? t(TranslationKey.Active) : t(TranslationKey.Inactive),
    },
  ]

  /**
   * Link Dealer form field configuration
   */
  const linkDealerFormField: FormFieldItem = {
    fieldType: FormFieldType.SEARCHABLE_DROPDOWN,
    items: dealerDropdownOptions,
    value: dealerToLink,
    onChange: (item: UIKit.DropdownItem | null) => {
      setDealerToLink(String(item?.value))
      setDealerFormFieldError('')
    },
    error: !!dealerFormFieldError,
    placeholder: t(TranslationKey['Choose a dealer to link to']),
    listboxHeight: 100,
  }

  /**
   * Contract Type form field configuration - additional section
   */
  const contractTypeAdditionalFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Lock fields']),
      text: contractTypeData?.lockFields ? t(TranslationKey.Yes) : t(TranslationKey.No),
    },

    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Display service price']),
      text: contractTypeData?.displayServicePrice ? t(TranslationKey.Yes) : t(TranslationKey.No),
    },

    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Display installation compensation']),
      text: contractTypeData?.displayInstallationCosts ? t(TranslationKey.Yes) : t(TranslationKey.No),
    },

    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Display extra costs']),
      text: contractTypeData?.displayExtraCosts ? t(TranslationKey.Yes) : t(TranslationKey.No),
    },
  ]

  /**
   * Contract Type form fields configuration - discount section
   */
  const contractTypeDiscountFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Product),
      text: formatPercentage(contractTypeData?.productDiscount || 0),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Accessories),
      text: formatPercentage(contractTypeData?.accessoryDiscount || 0),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Service),
      text: formatPercentage(contractTypeData?.serviceDiscount || 0),
    },
  ]

  /**
   * Contract Type form fields configuration - Rate info
   */
  const contractTypeRateInfoFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Advanced payment']),
      text: String(contractTypeData?.advancedPayment),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Function interest']),
      text: formatPercentage(contractTypeData?.functionInterest || 0),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: TranslationKey.Interest,
      text: formatPercentage(contractTypeData?.interest || 0),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Insurance),
      text: formatPercentage(contractTypeData?.insurancePercentage || 0),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Product minimum insurance']),
      text: formatCurrency(contractTypeData?.productInsuranceMinimum || 0, currency),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Accessory minimum insurance']),
      text: formatCurrency(contractTypeData?.accessoryInsuranceMinimum || 0, currency),
    },
  ]

  /**
   * Contract Type form fields configuration - description section
   */
  const contractTypeDescriptionFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Description),
      text: contractTypeData?.note || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Disclaimer),
      text: contractTypeData?.disclaimer || '-',
    },
  ]

  /**
   * A function handling unlinking the Contract Type from a Dealer
   * @param dealerId Dealer ID
   */
  const onUnlink = (dealerId: string) => {
    unlinkDealerFromContractTypeMutation(
      {dealerId, contractTypeId: String(id)},
      {onSuccess: () => setSuccessMessage(t(TranslationKey['Successfully unlinked a dealer']))},
    )
  }

  /**
   * A function handling linking the Contract Type to a Dealer
   * @returns void
   */
  const onLink = () => {
    if (!dealerToLink) {
      setDealerFormFieldError('dealerToLink')
      setError({
        message: generateReadableErrorMessages(t, ['dealerToLink']),
        name: '',
      })
      return
    }

    setIsLinkModalShown(false)
    setDealerToLink(undefined)
    if (id)
      linkDealerToContractTypeMutation(
        {dealerId: dealerToLink, contractTypeId: id},
        {onSuccess: () => setSuccessMessage(t(TranslationKey['Successfully linked a dealer']))},
      )
  }

  /**
   * A function handling the Back button click
   */
  const onPressBack = (): void => {
    navigate(NavigationPathKey.CONTRACT_TYPE_SEARCH)
  }

  /**
   * A function handling Edit button click
   */
  const onPressEdit = (): void => {
    navigate(`${NavigationPathKey.CONTRACT_TYPE_CREATION}/${id}`)
  }

  /**
   * A function handling modal close
   */
  const onCloseLinkModal = () => {
    setIsLinkModalShown(false)
    setDealerToLink(undefined)
    setDealerFormFieldError('')
  }

  return {
    navigate,
    onPressEdit,
    onPressBack,
    setIsLinkModalShown,
    onLink,
    onCloseLinkModal,
    t,
    linkDealerFormField,
    isEditButtonVisible,
    contractTypeData,
    contractTypeFormFields,
    contractTypeDescriptionFormFields,
    contractTypeAdditionalFormFields,
    contractTypeDiscountFormFields,
    contractTypeRateInfoFormFields,
    isLinkModalShown,
    activeTab,
    dealerColumns,
    dealerRows,
    tabButtons,
    modalText,
  }
}
